import React, { useState } from 'react';
import { LuDatabase, LuMapPin, LuBarChart3, LuUsers, LuShield, LuGlobe, LuAlertCircle, LuCalendar, LuMap } from 'react-icons/lu';
import { MdOutlineAccessTime, MdOutlineLocationCity, MdOutlineFeedback } from 'react-icons/md';
import { FiCheckCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';
const AboutCDAP = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const features = [
    {
      icon: <LuDatabase size={48} />,
      title: "Comprehensive Data",
      description: "Close to 100,000 data points across 283 indicators and 15 sectors"
    },
    {
      icon: <LuMapPin size={48} />,
      title: "Geographic Coverage",
      description: "Spanning states, districts and cities across India from 2011 onwards"
    },
    {
      icon: <LuBarChart3 size={48} />,
      title: "Interactive Visualizations",
      description: "Maps, charts, and heat maps for easy exploratory analysis"
    },
    {
      icon: <LuUsers size={48} />,
      title: "User-Centric Design",
      description: "Intuitive interface with flexible tables and customizable views"
    },
    {
      icon: <LuShield size={48} />,
      title: "Standardized Data",
      description: "Rigorous data sharing standards and interoperability protocols"
    },
    {
      icon: <LuGlobe size={48} />,
      title: "Open Access",
      description: "Democratizing data delivery for better urban outcomes"
    }
  ];

  const capabilities = [
    "Visualize data on maps, bar charts, line charts, pie charts and choropleth heat maps",
    "Overlay and compare multiple indicators across geographies and time periods",
    "Filter cities by tier categories and Urban Local Body types",
    "Download data in standardized, machine-readable formats",
    "Create flexible tables for custom analysis",
    "Access sector-based and geography-based navigation"
  ];

  const sectors = [
    "City Profile", "Quality of Life", "Urban Economy", "Demographics",
    "Infrastructure", "Governance", "Environment", "Transportation",
    "Housing", "Health", "Education", "Public Safety",
    "Finance", "Social Services", "Urban Planning"
  ];

  const cardStyle = {
    borderRadius: '16px',
    border: '2px solid #e2e8f0',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    backgroundColor: 'white'
  };

  const handleCardHover = (e, isHovering) => {
    if (isHovering) {
      e.currentTarget.style.transform = 'translateY(-8px)';
      e.currentTarget.style.boxShadow = '0 12px 24px rgba(44, 82, 130, 0.15)';
      e.currentTarget.style.borderColor = '#5484c0';
    } else {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      e.currentTarget.style.borderColor = '#e2e8f0';
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div style={{ backgroundColor: '#f8fbff', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Hero Section */}
      <div
  style={{
    background: 'linear-gradient(135deg, #488ce6ff 0%, #8db5e6ff 100%)',
    color: 'white',
    padding: '30px 20px' 
  }}>
  {/* <div
  style={{
    color: '#2c5282',
    padding: '30px 20px',
    borderBottom: '2px solid #e2e8f0'
  }}
> */}
  

  <div style={{ maxWidth: '100%', margin: '0 auto' }}>
  <h1
    style={{
      fontSize: 'clamp(1.25rem, 3.5vw, 2rem)', 
      fontWeight: '700',
      marginBottom: '10px'
    }}
  >
    About CDAP
  </h1>
  <p
    style={{
      fontSize: 'clamp(0.9rem, 1.6vw, 1.1rem)', 
      marginBottom: '8px',
      opacity: '0.95'
    }}
  >
    Empowering data-driven urban development through accessible, interoperable city data
  </p>
</div>

</div>


      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        
        {/* What is CDAP */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            ...cardStyle,
            padding: 'clamp(24px, 4vw, 48px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          onMouseEnter={(e) => handleCardHover(e, true)}
          onMouseLeave={(e) => handleCardHover(e, false)}>
            <h2 style={{ color: '#2c5282', fontWeight: '700', marginBottom: '20px', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
              What is CDAP?
            </h2>
            <p style={{ color: '#4a5568', fontSize: 'clamp(1rem, 2vw, 1.125rem)', lineHeight: '1.7', marginBottom: '20px' }}>
              The City Data and Analytics Platform is a micro-site of the National Data and Analytics Platform (NDAP), 
              NITI Aayog's flagship initiative to improve access and use of government data.
            </p>
            <p style={{ color: '#4a5568', lineHeight: '1.7', marginBottom: '20px' }}>
              CDAP is a user-friendly web platform that aggregates and hosts datasets from across India's vast statistical 
              infrastructure, with a specific focus on city and other geographic-oriented thematic data. As of November 20, 2025, 
              CDAP hosts close to <strong>100,000 data points</strong> across <strong>283 indicators</strong> and 
              <strong> 15 sectors</strong>, covering data from 2011 onwards.
            </p>
            <p style={{ color: '#4a5568', lineHeight: '1.7', margin: '0' }}>
              By providing place-specific, data-based information, CDAP equips diverse stakeholders to make better decisions 
              and shape stronger urban outcomes. The platform democratizes data delivery by making government datasets readily 
              accessible, implementing rigorous data sharing standards, and providing a seamless user interface with user-friendly tools.
            </p>
          </div>
        </div>

        {/* Key Features */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h2 style={{ color: '#2c5282', fontWeight: '700', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
              Key Features
            </h2>
            <p style={{ color: '#718096' }}>Powerful tools for urban data analysis and visualization</p>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '24px' 
          }}>
            {features.map((feature, index) => (
              <div key={index} style={{ 
                ...cardStyle,
                padding: '32px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}>
                <div style={{ color: '#3182ce', marginBottom: '16px' }}>
                  {feature.icon}
                </div>
                <h5 style={{ color: '#2c5282', fontWeight: '600', marginBottom: '12px', fontSize: '1.125rem' }}>
                  {feature.title}
                </h5>
                <p style={{ color: '#718096', margin: '0', lineHeight: '1.6' }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Capabilities */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            ...cardStyle,
            padding: 'clamp(24px, 4vw, 48px)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          onMouseEnter={(e) => handleCardHover(e, true)}
          onMouseLeave={(e) => handleCardHover(e, false)}>
            <h2 style={{ color: '#2c5282', fontWeight: '700', marginBottom: '24px', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
              Platform Capabilities
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '16px' 
            }}>
              {capabilities.map((capability, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <FiCheckCircle size={20} style={{ color: '#3182ce', flexShrink: '0', marginTop: '2px' }} />
                  <span style={{ color: '#4a5568', lineHeight: '1.6' }}>{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Core Principles */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h2 style={{ color: '#2c5282', fontWeight: '700', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
              Our Core Principles
            </h2>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '24px' 
          }}>
            <div style={{ 
              ...cardStyle,
              padding: '32px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            onMouseEnter={(e) => handleCardHover(e, true)}
            onMouseLeave={(e) => handleCardHover(e, false)}>
              <h4 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '16px', fontSize: '1.25rem' }}>
                Accessible
              </h4>
              <p style={{ color: '#4a5568', margin: '0', lineHeight: '1.6' }}>
                Clean, machine-readable datasets that stakeholders can navigate through geographical anchors or thematic lenses. 
                Data can be visualized spatially and downloaded in standardized formats for easy use.
              </p>
            </div>
            <div style={{ 
              ...cardStyle,
              padding: '32px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            onMouseEnter={(e) => handleCardHover(e, true)}
            onMouseLeave={(e) => handleCardHover(e, false)}>
              <h4 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '16px', fontSize: '1.25rem' }}>
                Interoperable
              </h4>
              <p style={{ color: '#4a5568', margin: '0', lineHeight: '1.6' }}>
                Datasets are mapped to common geographical and temporal identifiers using the Ministry of Panchayati Raj 
                Local Government Directory Code, enabling cross-sectoral and geographical analyses.
              </p>
            </div>
            <div style={{ 
              ...cardStyle,
              padding: '32px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            onMouseEnter={(e) => handleCardHover(e, true)}
            onMouseLeave={(e) => handleCardHover(e, false)}>
              <h4 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '16px', fontSize: '1.25rem' }}>
                Interactive
              </h4>
              <p style={{ color: '#4a5568', margin: '0', lineHeight: '1.6' }}>
                Create flexible tables and visualizations for exploratory analysis. Plot 3-5 indicators simultaneously 
                to reveal overlaps, contrasts, and layered relationships in the data.
              </p>
            </div>
          </div>
        </div>

        {/* User Feedback Feature */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            ...cardStyle,
            padding: 'clamp(24px, 4vw, 40px)',
            background: 'linear-gradient(135deg, #edf7ff 0%, #f8fbff 100%)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '2px solid #bee3f8'
          }}
          onMouseEnter={(e) => handleCardHover(e, true)}
          onMouseLeave={(e) => handleCardHover(e, false)}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
              <MdOutlineFeedback size={40} style={{ color: '#3182ce', flexShrink: '0' }} />
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h3 style={{ color: '#2c5282', fontWeight: '600', marginBottom: '12px', fontSize: '1.25rem' }}>
                  User-Centric Approach
                </h3>
                <p style={{ color: '#4a5568', margin: '0', lineHeight: '1.6' }}>
                  CDAP centres the user experience in data delivery by hosting use-case relevant indicators and regularly 
                  soliciting user feedback. <strong>You can use the feedback feature available on the platform to suggest 
                  relevant datasets to be hosted onto CDAP.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>



        {/* Data Organization */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            ...cardStyle,
            padding: 'clamp(24px, 4vw, 48px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          onMouseEnter={(e) => handleCardHover(e, true)}
          onMouseLeave={(e) => handleCardHover(e, false)}>
            <h2 style={{ color: '#2c5282', fontWeight: '700', marginBottom: '20px', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
              Data Organization
            </h2>
            <p style={{ color: '#4a5568', marginBottom: '24px', lineHeight: '1.6' }}>
              Data on CDAP is organized in two key ways to ensure easy navigation and analysis:
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '20px' 
            }}>
              <div style={{ 
                padding: '24px',
                borderRadius: '12px',
                backgroundColor: '#f8fbff',
                border: '2px solid #d6e8ff',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#edf7ff';
                e.currentTarget.style.borderColor = '#5484c0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fbff';
                e.currentTarget.style.borderColor = '#d6e8ff';
              }}>
                <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LuBarChart3 size={24} />
                  Sector-Based Taxonomy
                </h5>
                <p style={{ color: '#4a5568', margin: '0', lineHeight: '1.6' }}>
                  Focusing on the profile of Indian cities, quality of life, and economic indicators. 
                  Each sector includes multiple sub-sectors with searchable functionality.
                </p>
              </div>
              <div style={{ 
                padding: '24px',
                borderRadius: '12px',
                backgroundColor: '#f8fbff',
                border: '2px solid #d6e8ff',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#edf7ff';
                e.currentTarget.style.borderColor = '#5484c0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fbff';
                e.currentTarget.style.borderColor = '#d6e8ff';
              }}>
                <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LuMapPin size={24} />
                  Geographic Navigation
                </h5>
                <p style={{ color: '#4a5568', margin: '0', lineHeight: '1.6' }}>
                  Interactive spatial maps with administrative boundaries from national level down to 
                  states, districts, cities and beyond for intuitive location-based exploration.
                </p>
              </div>
            </div>
          </div>
        </div>

    {/* Source Attribution and Spatial Alignment - Always Expanded */}
<div style={{ marginBottom: '48px' }}>
  <div style={{ 
    ...cardStyle,
    padding: 'clamp(24px, 4vw, 48px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  }}
  onMouseEnter={(e) => handleCardHover(e, true)}
  onMouseLeave={(e) => handleCardHover(e, false)}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
      <h2 style={{ color: '#2c5282', fontWeight: '700', margin: '0', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
        Source Attribution & Spatial Alignment Protocols
      </h2>
    </div>

    {/* LGD Section */}
    <div 
      style={{ 
        padding: '20px',
        marginBottom: '16px',
        borderRadius: '12px',
        backgroundColor: '#f7fafc',
        border: '2px solid #e2e8f0',
        transition: 'all 0.3s ease'
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h4 style={{ color: '#2c5282', fontWeight: '600', margin: '0', fontSize: '1.125rem' }}>
            Use of the Local Government Directory (LGD)
          </h4>
        </div>
      </div>
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
        
        {/* LGD as Geographic Anchor subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            LGD as Geographic Anchor
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', marginBottom: '0' }}>
            <strong>CDAP uses the LGD directory as an anchor for geographies.</strong> The data is current as of <strong>July 10, 2025</strong>.
          </p>
        </div>

        {/* ULB Count Variations subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            ULB Count Variations
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            It should be noted that the <strong>ULB count differs across various sections of the LGD website</strong>. For CDAP, the source used is <strong>"All Urban Local Body of India."</strong>
          </p>
        </div>

        {/* District Count Variations subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            District Count Variations
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            It should be noted that the <strong>district count differs across various sections of the LGD website</strong>. For CDAP, the source used is <strong>"All Districts of India."</strong>
          </p>
        </div>

        {/* ULB Type Classification subsection */}
        <div>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            ULB Type Classification
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            CDAP uses the Urban Local Government type from the LGD directory. The <strong>ULB type has been sourced from the LGD file titled "State and District Wise ULBs."</strong> Some ULBs present in the main file but missing 
            in this reference file were searched and identified manually on the LGD website.
          </p>
        </div>
      </div>
    </div>

    {/* Maps Section */}
    <div 
      style={{ 
        padding: '20px',
        marginBottom: '16px',
        borderRadius: '12px',
        backgroundColor: '#f7fafc',
        border: '2px solid #e2e8f0',
        transition: 'all 0.3s ease'
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h4 style={{ color: '#2c5282', fontWeight: '600', margin: '0', fontSize: '1.125rem' }}>
            Maps
          </h4>
        </div>
      </div>
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
        
        {/* Source of Maps subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Source of Maps
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', marginBottom: '12px' }}>
            The shapefiles for State and District levels were sourced from the <strong>Survey of India (SOI)</strong>, 
            current as of <strong>June 18, 2025</strong>. Each shapefile is associated with LGD codes, which serve as the common geographic identifiers to map datasets accurately to locations.
          </p>
          <div style={{ 
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: '#edf7ff',
            border: '1px solid #bee3f8',
            marginBottom: '12px'
          }}>
            <p style={{ color: '#4a5568', margin: '0', fontSize: '0.9rem', lineHeight: '1.5' }}>
              <strong>City Shapefiles:</strong> CDAP does not have city shapefiles; instead, we use latitude and longitude to represent city data 
              (mainly from the City Finance source). For tier-1 and tier-2 cities where lat/longs are not available, we use a central point or 
              famous monument of the city as a reference.
            </p>
          </div>
        </div>

        {/* Boundary Changes Over Time subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Boundary Changes Over Time
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', marginBottom: '0' }}>
            As administrative boundaries change—through mergers, splits, or reclassification—older geographies may not appear on current CDAP maps. 
            Since CDAP aligns with the latest LGD codes, historical data will still be <strong>available for download and visualization 
            in non-spatial formats</strong>, but may not always be represented spatially.
          </p>
        </div>

        {/* Boundary Misalignments subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Boundary Misalignments
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            SOI shapefiles contained instances where some district polygons did not align with their respective state boundaries. 
            CDAP strives to correct these by realigning district boundaries to their appropriate states, without altering the original 
            geometry or attributes. <strong>This should be treated as a caveat when using CDAP maps.</strong>
          </p>
        </div>

        {/* Errors in District Shapefiles subsection */}
        <div>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Errors in District Shapefiles
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            Certain errors in the SOI district shapefiles are under review, including incorrect LGD codes and districts of one 
            state appearing within another state's boundaries.
          </p>
        </div>
      </div>
    </div>

    {/* Data Dates Section */}
    <div 
      style={{ 
        padding: '20px',
        borderRadius: '12px',
        backgroundColor: '#f7fafc',
        border: '2px solid #e2e8f0',
        transition: 'all 0.3s ease'
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h4 style={{ color: '#2c5282', fontWeight: '600', margin: '0', fontSize: '1.125rem' }}>
            Data Dates
          </h4>
        </div>
      </div>
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
        
        {/* Overview subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Overview
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', marginBottom: '0' }}>
            CDAP has two date fields. This is because some reports/data sources contain/use different indicators from different years.
          </p>
        </div>

        {/* Metadata Date subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Metadata Date
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            This reflects the year the report/overall data source was published or the period of data collection where available, 
            whether on a website, PDF, dashboard, etc.
          </p>
        </div>

        {/* Indicator Date subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Indicator Date
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            This reflects the actual date/year of the indicator itself.
          </p>
        </div>

        {/* Default Dating Convention subsection */}
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Default Dating Convention
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            By default, the data is dated according to the metadata source, unless an alternative date 
            is explicitly provided within the source. In such cases, the indicator date reflects the alternative date.
          </p>
        </div>

        {/* Usage in Analysis subsection */}
        <div>
          <h5 style={{ color: '#3182ce', fontWeight: '600', marginBottom: '12px', fontSize: '1rem' }}>
            Usage in Analysis
          </h5>
          <p style={{ color: '#4a5568', lineHeight: '1.6', margin: '0' }}>
            For all data analysis, chart/graph plotting, and map visualizations, the indicator date 
            will be used if available rather than the metadata date.
          </p>
        </div>
      </div>
    </div>
  </div>
</div>

        {/* Attribution */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #2c5282 0%, #3182ce 100%)',
            color: 'white',
            padding: 'clamp(32px, 5vw, 48px)',
            textAlign: 'center',
            boxShadow: '0 8px 16px rgba(44, 82, 130, 0.3)',
            border: '2px solid rgba(255, 255, 255, 0.2)'
          }}>
         
            <p style={{ marginBottom: '24px', fontSize: 'clamp(0.95rem, 2vw, 1.125rem)', lineHeight: '1.7' }}>
              CDAP is a micro-site of NDAP and is conceived, created and operated by 
              <strong> Janaagraha Centre for Citizenship and Democracy</strong> with thanks for technical 
              collaboration with <strong>Social Bytes</strong> and <strong>IBM through the IBM Impact Accelerator</strong>.
            </p>
            {/* <p style={{ margin: '0', fontSize: '0.9rem', opacity: '0.9' }}>
              Part of NITI Aayog's initiative to democratize government data access across India
            </p> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutCDAP;


